// TODO: the whole component might be reused with Resources Filters
import cx from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import ComboBoxMultiple from "components/ComboBoxMultiple";
import ComboBoxMultipleDefaultItem from "components/ComboBoxMultiple/ComboBoxMultipleDefaultItem";
import { Cross } from "components/icons/generated";
import Box from "ds/components/Box";
import ComboBox from "ds/components/ComboBox";
import ComboBoxDefaultItem from "ds/components/ComboBox/DefaultItem";
import Icon from "ds/components/Icon";
import useOutsideClick from "hooks/useOutsideClick";
import { withEnterKeyPress } from "utils/browser";

import { translateLabel } from "../helpers";
import { generateFilterButtonText } from "./helpers";
import { FilterOption, PossibleValues } from "./types";

import "./styles.css";

type ResourceFiltersProps = {
  index: number;
  filterField: string[];
  filterValues: FilterOption[][];
  handleFilterChange: (option: FilterOption | null, index: number) => unknown;
  handleMultiFilterChange: (newValues: FilterOption[], index: number) => unknown;
  keyPossibleValues: PossibleValues;
  empty?: boolean;
};

const ResourceFilters = (props: ResourceFiltersProps) => {
  const {
    filterField,
    filterValues,
    handleFilterChange,
    handleMultiFilterChange,
    index,
    keyPossibleValues,
  } = props;
  const [isEditing, setIsEditing] = useState(true);
  const wrapperRef = useRef(null);

  const keyOptions = useMemo(
    () =>
      Object.keys(keyPossibleValues || {})
        .map((value) => ({
          value,
          // TODO: remove while refactoring Runs helper
          label: translateLabel(value),
        }))
        .sort(),
    [keyPossibleValues]
  );

  const filterKeyOptions = useMemo(
    () =>
      filterField[index] === ""
        ? undefined
        : {
            value: filterField[index],
            // TODO: remove while refactoring Runs helper
            label: translateLabel(filterField[index]) as string,
          },
    [filterField, index]
  );

  const filterValuesOptions = useMemo(() => {
    if (filterField[index] === "") {
      return [];
    } else if (keyPossibleValues && !keyPossibleValues[filterField[index]]) {
      return [];
    }

    return keyPossibleValues[filterField[index]].map((value) => {
      return {
        value: value.toString(),
        label: typeof value === "boolean" ? `${(value && "true") || "false"}` : value,
      };
    });
  }, [filterField, keyPossibleValues, index]);

  const selectedFilterValues = useMemo(
    () => filterValues[index]?.map((i) => i.value) ?? [],
    [filterValues, index]
  );

  const hasValues = filterValues[index]?.length > 0;
  const buttonText = useMemo(
    () => generateFilterButtonText(filterKeyOptions, filterValues, index),
    [filterKeyOptions, filterValues, index]
  );

  const buttonClass = cx("resources-filters__value", {
    "resources-filters__value--empty": !filterKeyOptions,
  });
  const wrapperClass = cx({
    "resources-filters__wrapper--empty": !filterKeyOptions && index === 0,
  });

  const clearValues = useCallback(() => {
    handleFilterChange(null, index);
  }, [handleFilterChange, index]);

  const closeEditable = () => {
    if (!filterKeyOptions && filterValues[index]?.length === 0) {
      clearValues();
      setIsEditing(false);
    }
    if (filterKeyOptions && filterValues[index]?.length >= 0) {
      setIsEditing(false);
    }
  };

  const handleClick = () => setIsEditing(true);

  useOutsideClick(wrapperRef, closeEditable);
  useEffect(() => {
    if ((filterKeyOptions && filterValues[index]?.length >= 0) || filterField.length === 1) {
      setIsEditing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterKeyChange = useCallback(
    (value: string | null) => {
      const option = keyOptions.find((option) => option.value === value);
      handleFilterChange(option ?? null, index);
    },
    [handleFilterChange, index, keyOptions]
  );

  const handleFilterValueChange = useCallback(
    (value: string | null) => {
      if (!value) return;
      const values = filterValues[index];
      const wasSelected = Boolean(values.find((item) => item.value === value));
      let newValues: FilterOption[];

      if (wasSelected) {
        newValues = values.filter((item) => item.value !== value);
      } else {
        newValues = [...values, { label: value, value }];
      }
      handleMultiFilterChange(newValues, index);
    },
    [filterValues, index, handleMultiFilterChange]
  );

  return (
    <div ref={wrapperRef} className={wrapperClass}>
      <div
        className={buttonClass}
        onClick={handleClick}
        onKeyDown={withEnterKeyPress(handleClick)}
        role="button"
        tabIndex={0}
      >
        {buttonText}
        {hasValues && (
          <button type="button" className="resources-filters__delete" onClick={clearValues}>
            <Icon src={Cross} />
          </button>
        )}
      </div>
      {isEditing && (
        <Box direction="column" gap="medium" className="resource-filters__form">
          <ComboBox
            value={filterKeyOptions?.value ?? ""}
            items={keyOptions}
            onChange={handleFilterKeyChange}
            placeholder="Filter key"
          >
            {(item) => <ComboBoxDefaultItem id={item.value} {...item} />}
          </ComboBox>
          <ComboBoxMultiple
            values={selectedFilterValues}
            items={filterValuesOptions}
            onChange={handleFilterValueChange}
            placeholder="Filtered values"
          >
            {(item) => <ComboBoxMultipleDefaultItem checkboxVisible id={item.value} {...item} />}
          </ComboBoxMultiple>
        </Box>
      )}
    </div>
  );
};

export default ResourceFilters;
